import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "../LanguageContext";
import { scrollToId } from "../scroll";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 1.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-forest">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="/images/hero.jpg"
          alt=""
          className="h-full w-full object-cover opacity-40"
        />
      </motion.div>
      <div className="absolute inset-0 bg-forest/45" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(60% 50% at 78% 18%, rgba(212,163,89,0.22), transparent 70%)" }}
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 sm:px-8 lg:px-12">
        <div className="overflow-hidden">
          <motion.p
            variants={lineVariants}
            initial="hidden"
            animate="visible"
            custom={0}
            className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold sm:text-xs"
          >
            {t.hero.overline}
          </motion.p>
        </div>

        <h1 className="mt-8 font-serif font-light leading-[1.04] tracking-tight text-linen">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                custom={i + 1}
                className={`block text-[clamp(2.4rem,7vw,6rem)] ${
                  i === 1 ? "italic text-sage" : ""
                } ${i === 2 ? "mt-4 text-[clamp(1.6rem,4vw,3.2rem)] italic text-gold" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-2xl text-[1.7rem] font-light leading-snug text-linen/75 sm:text-[1.9rem]"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollToId("chi-sono")}
            className="group flex items-center gap-3 rounded-full bg-terra px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-linen transition-colors duration-300 hover:bg-gold hover:text-forest"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollToId("contatti")}
            className="rounded-full border border-linen/30 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-linen transition-colors duration-300 hover:bg-linen/10"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-linen/60 md:flex"
      >
        {t.hero.scrollHint}
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown className="h-3.5 w-3.5" strokeWidth={1.5} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
